import React, { useState } from 'react';
import {
  Type,
  Palette,
  Image,
  Layout,
  MousePointer,
  Eye,
  EyeOff,
  Plus,
  Trash2,
  Upload,
  Settings,
  Layers,
  Grid,
  AlignLeft,
  AlignCenter,
  Sparkles,
  Play,
  Monitor,
  Smartphone,
  Clock,
  MapPin,
  Info,
  ChevronDown,
  ChevronUp,
  Wand2
} from 'lucide-react';

// Import your actual components
import ColorPicker from "../../shared/ColorPicker";
import MediaLibrary from "../../shared/MediaUploader";

const HeaderDividedForm = ({ content = {}, updateContent = () => { }, pageSlug = 'demo' }) => {
  const [activeTab, setActiveTab] = useState('content');
  const [showPreview, setShowPreview] = useState(false);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [currentImageField, setCurrentImageField] = useState(null);
  const [currentMediaType, setCurrentMediaType] = useState('image');
  const [expandedSections, setExpandedSections] = useState({
    content: true,
    buttons: false,
    colors: false,
    images: false,
    layout: false
  });

  const handleSelectImage = (imageUrl) => {
    if (currentImageField) {
      if (currentImageField.includes('[') && currentImageField.includes(']')) {
        // Handle category item images
        const match = currentImageField.match(/categoryItems\[(\d+)\]\.imageUrl/);
        if (match) {
          const index = parseInt(match[1]);
          handleCategoryChange(index, 'imageUrl', imageUrl);
        }
      } else {
        updateContent(currentImageField, imageUrl);
      }
      setShowMediaLibrary(false);
      setCurrentImageField(null);
    }
  };

  const openMediaLibrary = (fieldName, mediaType = 'image') => {
    setCurrentImageField(fieldName);
    setCurrentMediaType(mediaType);
    setShowMediaLibrary(true);
  };

  const handleCategoryChange = (index, field, value) => {
    const newCategories = [...(content.categoryItems || [])];
    if (!newCategories[index]) {
      newCategories[index] = {};
    }
    newCategories[index][field] = value;
    updateContent('categoryItems', newCategories);
  };

  const addCategoryItem = () => {
    const newCategories = [...(content.categoryItems || [])];
    newCategories.push({ name: '', imageUrl: '', link: '' });
    updateContent('categoryItems', newCategories);
  };

  const removeCategoryItem = (index) => {
    const newCategories = [...(content.categoryItems || [])];
    newCategories.splice(index, 1);
    updateContent('categoryItems', newCategories);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const tabs = [
    { id: 'content', label: 'Content', icon: Type },
    { id: 'buttons', label: 'Buttons', icon: MousePointer },
    { id: 'design', label: 'Design', icon: Palette },
    { id: 'media', label: 'Media', icon: Image },
    { id: 'layout', label: 'Layout', icon: Layout }
  ];

  const buttonStyles = [
    { value: 'default', name: 'Rounded Pills', preview: 'rounded-full' },
    { value: 'square', name: 'Square Large', preview: 'rounded-lg' }
  ];

  const layoutOptions = [
    { value: '1/2', name: '50%', visual: 'w-1/2' },
    { value: '1/3', name: '33%', visual: 'w-1/3' },
    { value: '2/3', name: '67%', visual: 'w-2/3' },
    { value: '1/4', name: '25%', visual: 'w-1/4' },
    { value: '3/4', name: '75%', visual: 'w-3/4' },
    { value: 'full', name: '100%', visual: 'w-full' },
    { value: '0', name: 'Hidden', visual: 'w-0' }
  ];

  if (showMediaLibrary) {
    return (
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold">Media Library</h3>
            <button
              onClick={() => setShowMediaLibrary(false)}
              className="bg-white/20 hover:bg-white/30 transition-colors px-4 py-2 rounded-lg"
            >
              Close
            </button>
          </div>
        </div>
        <div className="p-6">
          <MediaLibrary
            onUploadComplete={handleSelectImage}
            type={currentMediaType}
            pageSlug={pageSlug}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Wand2 className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Header Divided Section Builder</h2>
              <p className="text-indigo-100">Create stunning split-layout headers with advanced customization</p>
            </div>
          </div>
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="bg-white/20 hover:bg-white/30 transition-colors px-4 py-2 rounded-lg flex items-center gap-2"
          >
            {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {showPreview ? 'Hide' : 'Show'} Preview
          </button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row">
        {/* Form Section */}
        <div className="flex-1 p-6">
          {/* Navigation Tabs */}
          <div className="flex flex-wrap bg-gray-100 rounded-xl p-1 mb-6">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 min-w-[120px] flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${activeTab === tab.id
                    ? 'bg-white text-indigo-600 shadow-md'
                    : 'text-gray-600 hover:text-gray-800'
                  }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content Tab */}
          {activeTab === 'content' && (
            <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
              {/* Main Content */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4">
                  <Type className="w-5 h-5 text-indigo-600" />
                  Main Content
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Hero Title</label>
                    <textarea
                      value={content?.title || 'Freshly <span>Baked,<br/> Daily</span> Served'}
                      onChange={(e) => updateContent('title', e.target.value)}
                      className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-0 transition-colors resize-none"
                      rows="3"
                      placeholder="Enter your main title..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Use &lt;span&gt;text&lt;/span&gt; to highlight and &lt;br/&gt; for line breaks
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
                    <textarea
                      value={content?.subtitle || 'Our bakery and pastry products are produced each day for the best quality and taste.'}
                      onChange={(e) => updateContent('subtitle', e.target.value)}
                      className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-0 transition-colors resize-none"
                      rows="3"
                      placeholder="Add a supporting subtitle..."
                    />
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                      <Clock className="w-4 h-4 text-indigo-500" />
                      Hours Badge Text
                    </label>
                    <input
                      type="text"
                      value={content?.hoursText || 'Open Today: <span>7:00 AM - 10:00 PM</span>'}
                      onChange={(e) => updateContent('hoursText', e.target.value)}
                      className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-0 transition-colors"
                      placeholder="Enter hours information..."
                    />
                  </div>

                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={content?.iconVisible !== false}
                        onChange={(e) => updateContent('iconVisible', e.target.checked)}
                        className="mr-2 w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <span className="text-sm font-medium">Show Icon</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={content?.hoursBadgeVisible !== false}
                        onChange={(e) => updateContent('hoursBadgeVisible', e.target.checked)}
                        className="mr-2 w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <span className="text-sm font-medium">Show Hours Badge</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Overlay Card Settings */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl border">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800">
                    <Layers className="w-5 h-5 text-emerald-600" />
                    Overlay Card
                  </h3>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={content?.overlayCardVisible !== false}
                      onChange={(e) => updateContent('overlayCardVisible', e.target.checked)}
                      className="mr-2 w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                    />
                    <span className="text-sm font-medium">Enable</span>
                  </label>
                </div>

                {content?.overlayCardVisible !== false && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Card Title</label>
                      <input
                        type="text"
                        value={content?.overlayCardTitle || 'Try Our Specialty'}
                        onChange={(e) => updateContent('overlayCardTitle', e.target.value)}
                        className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:ring-0 transition-colors"
                        placeholder="Featured item title..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Card Description</label>
                      <textarea
                        value={content?.overlayCardText || 'Mix Berry Coffee Cake fresh throughout the day'}
                        onChange={(e) => updateContent('overlayCardText', e.target.value)}
                        className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:ring-0 transition-colors resize-none"
                        rows="3"
                        placeholder="Describe your featured item..."
                      />
                      <p className="text-xs text-gray-500 mt-1">HTML tags supported: &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;br&gt;</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Card Tagline</label>
                      <input
                        type="text"
                        value={content?.overlayCardTagline || '→ Featured Today'}
                        onChange={(e) => updateContent('overlayCardTagline', e.target.value)}
                        className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:ring-0 transition-colors"
                        placeholder="Add a tagline..."
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Categories Section */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-xl border">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800">
                    <Grid className="w-5 h-5 text-purple-600" />
                    Categories
                  </h3>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={content?.showCategories !== false}
                        onChange={(e) => updateContent('showCategories', e.target.checked)}
                        className="mr-2 w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm font-medium">Enable</span>
                    </label>
                    <button
                      onClick={addCategoryItem}
                      className="bg-purple-600 text-white px-3 py-1 rounded-lg hover:bg-purple-700 transition-colors text-sm flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" />
                      Add Category
                    </button>
                  </div>
                </div>

                {content?.showCategories !== false && (
                  <div className="space-y-3">
                    {(content?.categoryItems || []).map((item, index) => (
                      <div key={index} className="bg-white p-4 rounded-lg border shadow-sm">
                        <div className="flex justify-between items-center mb-3">
                          <span className="font-medium text-sm text-gray-700">Category {index + 1}</span>
                          <button
                            onClick={() => removeCategoryItem(index)}
                            className="text-red-600 hover:text-red-800 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                            <input
                              type="text"
                              value={item.name || ''}
                              onChange={(e) => handleCategoryChange(index, 'name', e.target.value)}
                              className="w-full p-2 border rounded-lg text-sm focus:border-purple-500 focus:ring-0"
                              placeholder="Category name..."
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Link</label>
                            <input
                              type="text"
                              value={item.link || ''}
                              onChange={(e) => handleCategoryChange(index, 'link', e.target.value)}
                              className="w-full p-2 border rounded-lg text-sm focus:border-purple-500 focus:ring-0"
                              placeholder="/category-url"
                            />
                          </div>
                        </div>

                        <div className="mt-3">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Image</label>
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={item.imageUrl || ''}
                              onChange={(e) => handleCategoryChange(index, 'imageUrl', e.target.value)}
                              className="flex-1 p-2 border rounded-lg text-sm focus:border-purple-500 focus:ring-0"
                              placeholder="Image URL..."
                            />
                            <button
                              onClick={() => {
                                setCurrentImageField(`categoryItems[${index}].imageUrl`);
                                setCurrentMediaType('image');
                                setShowMediaLibrary(true);
                              }}
                              className="bg-purple-600 text-white px-3 py-2 rounded-lg hover:bg-purple-700 transition-colors text-xs"
                            >
                              Browse
                            </button>
                          </div>
                          {item.imageUrl && (
                            <div className="mt-2">
                              <img
                                src={item.imageUrl}
                                alt={`Category ${index + 1}`}
                                className="w-16 h-16 object-cover rounded-lg border"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    {(!content?.categoryItems || content.categoryItems.length === 0) && (
                      <div className="text-center p-6 bg-white rounded-lg border-2 border-dashed border-gray-200">
                        <Grid className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-600 font-medium">No categories added yet</p>
                        <p className="text-sm text-gray-500">Click "Add Category" to create your first category</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Buttons Tab */}
          {activeTab === 'buttons' && (
            <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
              {/* Button Content */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4">
                  <MousePointer className="w-5 h-5 text-indigo-600" />
                  Button Content
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { key: 'primary', label: 'Primary Button', defaultText: 'View Our Menu', defaultLink: '/menu' },
                    { key: 'secondary', label: 'Secondary Button', defaultText: 'Order Online', defaultLink: 'https://anorder.ca/1781/paneformaggio' },
                    { key: 'third', label: 'Third Button', defaultText: '', defaultLink: '' },
                    { key: 'fourth', label: 'Fourth Button', defaultText: '', defaultLink: '' }
                  ].map(button => (
                    <div key={button.key} className="bg-white p-4 rounded-lg border">
                      <h4 className="font-medium text-gray-800 mb-3">{button.label}</h4>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Button Text</label>
                          <input
                            type="text"
                            value={content?.[`${button.key}ButtonText`] || button.defaultText}
                            onChange={(e) => updateContent(`${button.key}ButtonText`, e.target.value)}
                            className="w-full p-2 border rounded focus:border-indigo-500 focus:ring-0 text-sm"
                            placeholder={button.defaultText || 'Button text...'}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Button Link</label>
                          <input
                            type="text"
                            value={content?.[`${button.key}ButtonLink`] || button.defaultLink}
                            onChange={(e) => updateContent(`${button.key}ButtonLink`, e.target.value)}
                            className="w-full p-2 border rounded focus:border-indigo-500 focus:ring-0 text-sm"
                            placeholder={button.defaultLink || '/button-url'}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Icon</label>
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={content?.[`${button.key}ButtonIcon`] || ''}
                              onChange={(e) => updateContent(`${button.key}ButtonIcon`, e.target.value)}
                              className="flex-1 p-2 border rounded focus:border-indigo-500 focus:ring-0 text-sm"
                              placeholder="Icon URL..."
                            />
                            <button
                              onClick={() => openMediaLibrary(`${button.key}ButtonIcon`, 'image')}
                              className="bg-indigo-600 text-white px-2 py-1 rounded text-xs hover:bg-indigo-700 transition-colors"
                            >
                              Browse
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Button Settings */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl border">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4">
                  <Settings className="w-5 h-5 text-emerald-600" />
                  Button Settings
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Button Style</label>
                    <div className="space-y-2">
                      {buttonStyles.map(style => (
                        <button
                          key={style.value}
                          onClick={() => updateContent('buttonStyle', style.value)}
                          className={`w-full p-3 rounded-lg border-2 transition-all text-left ${content?.buttonStyle === style.value
                              ? 'border-emerald-500 bg-emerald-50'
                              : 'border-gray-200 hover:border-gray-300'
                            }`}
                        >
                          <div className="font-medium text-sm">{style.name}</div>
                          <div className={`mt-1 h-6 bg-emerald-600 text-white text-xs flex items-center justify-center ${style.preview}`}>
                            Preview
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Icon Position</label>
                    <select
                      value={content?.buttonIconPosition || 'before-text'}
                      onChange={(e) => updateContent('buttonIconPosition', e.target.value)}
                      className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:ring-0"
                    >
                      <option value="before-text">Before Text (Left)</option>
                      <option value="top-center">Top Center</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Icon Size</label>
                    <input
                      type="text"
                      value={content?.buttonIconSize || '20px'}
                      onChange={(e) => updateContent('buttonIconSize', e.target.value)}
                      className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:ring-0"
                      placeholder="e.g. 20px"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Button Height</label>
                    <input
                      type="text"
                      value={content?.buttonHeight || ''}
                      onChange={(e) => updateContent('buttonHeight', e.target.value)}
                      className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:ring-0"
                      placeholder="e.g. 64px (default if empty)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Smartphone className="w-4 h-4 inline mr-1" />
                      Mobile Layout
                    </label>
                    <select
                      value={content?.mobileButtonLayout || 'horizontal'}
                      onChange={(e) => updateContent('mobileButtonLayout', e.target.value)}
                      className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:ring-0"
                    >
                      <option value="horizontal">Horizontal</option>
                      <option value="vertical">Vertical</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Button Layout</label>
                    <select
                      value={content?.buttonLayoutExtended || 'default'}
                      onChange={(e) => updateContent('buttonLayoutExtended', e.target.value)}
                      className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:ring-0"
                    >
                      <option value="default">Left Side Only</option>
                      <option value="extended">Across Both Sides</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Design Tab */}
          {activeTab === 'design' && (
            <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
              {/* Color Presets */}
              <div className="bg-gradient-to-r from-violet-50 to-purple-50 p-6 rounded-xl border">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4">
                  <Sparkles className="w-5 h-5 text-violet-600" />
                  Quick Color Themes
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { name: 'Warm Bakery', bg: '#f8f3e9', accent: '#d17b34', text: '#5c3618' },
                    { name: 'Modern Blue', bg: '#f0f9ff', accent: '#3b82f6', text: '#1e40af' },
                    { name: 'Fresh Green', bg: '#f0fdf4', accent: '#22c55e', text: '#166534' },
                    { name: 'Elegant Gray', bg: '#f9fafb', accent: '#6b7280', text: '#374151' }
                  ].map(theme => (
                    <button
                      key={theme.name}
                      onClick={() => {
                        updateContent('leftBackgroundColor', theme.bg);
                        updateContent('primaryButtonBackgroundColor', theme.accent);
                        updateContent('titleColor', theme.text);
                        updateContent('highlightColor', theme.accent);
                      }}
                      className="p-3 rounded-lg border-2 border-gray-200 hover:border-violet-400 transition-all group"
                    >
                      <div className="flex space-x-1 mb-2">
                        <div className="w-4 h-4 rounded" style={{ backgroundColor: theme.bg }}></div>
                        <div className="w-4 h-4 rounded" style={{ backgroundColor: theme.accent }}></div>
                        <div className="w-4 h-4 rounded" style={{ backgroundColor: theme.text }}></div>
                      </div>
                      <p className="text-xs font-medium group-hover:text-violet-600">{theme.name}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Background Colors */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4">
                  <Palette className="w-5 h-5 text-blue-600" />
                  Section Backgrounds
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-8 h-8 rounded-full border-2 border-gray-300"
                        style={{ backgroundColor: content?.leftBackgroundColor || '#f8f3e9' }}
                      ></div>
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Left Section Background</label>
                        <ColorPicker
                          color={content?.leftBackgroundColor || '#f8f3e9'}
                          onChange={(color) => updateContent('leftBackgroundColor', color)}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-8 h-8 rounded-full border-2 border-gray-300"
                        style={{ backgroundColor: content?.rightBackgroundColor || '#5c3618' }}
                      ></div>
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Right Section Background</label>
                        <ColorPicker
                          color={content?.rightBackgroundColor || '#5c3618'}
                          onChange={(color) => updateContent('rightBackgroundColor', color)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Text Colors */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl border">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4">
                  <Type className="w-5 h-5 text-emerald-600" />
                  Text Colors
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { key: 'titleColor', label: 'Main Title', default: '#5c3618', preview: 'Main Heading' },
                    { key: 'subtitleColor', label: 'Subtitle', default: '#6b5c4d', preview: 'Subtitle text' },
                    { key: 'highlightColor', label: 'Highlight/Accent', default: '#d17b34', preview: 'Highlighted text' }
                  ].map(item => (
                    <div key={item.key} className="bg-white p-4 rounded-lg border">
                      <div className="flex items-center gap-2 mb-3">
                        <div
                          className="w-6 h-6 rounded-full border-2 border-gray-300"
                          style={{ backgroundColor: content?.[item.key] || item.default }}
                        ></div>
                        <label className="text-sm font-medium text-gray-700">{item.label}</label>
                      </div>
                      <div
                        className="text-sm mb-2 p-2 rounded bg-gray-50"
                        style={{ color: content?.[item.key] || item.default }}
                      >
                        {item.preview}
                      </div>
                      <ColorPicker
                        color={content?.[item.key] || item.default}
                        onChange={(color) => updateContent(item.key, color)}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Button Colors */}
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-6 rounded-xl border">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4">
                  <MousePointer className="w-5 h-5 text-orange-600" />
                  Button Colors
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Primary Button */}
                  <div className="bg-white p-4 rounded-lg border">
                    <h4 className="font-medium text-gray-800 mb-3">Primary Button</h4>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-6 h-6 rounded border-2 border-gray-300"
                          style={{ backgroundColor: content?.primaryButtonBackgroundColor || '#d17b34' }}
                        ></div>
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Background</label>
                          <ColorPicker
                            color={content?.primaryButtonBackgroundColor || '#d17b34'}
                            onChange={(color) => updateContent('primaryButtonBackgroundColor', color)}
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-6 h-6 rounded border-2 border-gray-300"
                          style={{ backgroundColor: content?.primaryButtonTextColor || '#ffffff' }}
                        ></div>
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Text</label>
                          <ColorPicker
                            color={content?.primaryButtonTextColor || '#ffffff'}
                            onChange={(color) => updateContent('primaryButtonTextColor', color)}
                          />
                        </div>
                      </div>
                      {/* Preview */}
                      <div
                        className="px-4 py-2 rounded-lg text-center text-sm font-medium"
                        style={{
                          backgroundColor: content?.primaryButtonBackgroundColor || '#d17b34',
                          color: content?.primaryButtonTextColor || '#ffffff'
                        }}
                      >
                        Primary Button
                      </div>
                    </div>
                  </div>

                  {/* Secondary Button */}
                  <div className="bg-white p-4 rounded-lg border">
                    <h4 className="font-medium text-gray-800 mb-3">Secondary Button</h4>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-6 h-6 rounded border-2"
                          style={{
                            backgroundColor: content?.secondaryButtonBackgroundColor || 'transparent',
                            borderColor: content?.secondaryButtonBorderColor || '#d17b34'
                          }}
                        ></div>
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Border</label>
                          <ColorPicker
                            color={content?.secondaryButtonBorderColor || '#d17b34'}
                            onChange={(color) => updateContent('secondaryButtonBorderColor', color)}
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-6 h-6 rounded border-2 border-gray-300"
                          style={{ backgroundColor: content?.secondaryButtonTextColor || '#d17b34' }}
                        ></div>
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Text</label>
                          <ColorPicker
                            color={content?.secondaryButtonTextColor || '#d17b34'}
                            onChange={(color) => updateContent('secondaryButtonTextColor', color)}
                          />
                        </div>
                      </div>
                      {/* Preview */}
                      <div
                        className="px-4 py-2 rounded-lg text-center text-sm font-medium border-2"
                        style={{
                          borderColor: content?.secondaryButtonBorderColor || '#d17b34',
                          color: content?.secondaryButtonTextColor || '#d17b34',
                          backgroundColor: content?.secondaryButtonBackgroundColor || 'transparent'
                        }}
                      >
                        Secondary Button
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Hours Badge Colors */}
              <div className="bg-gradient-to-r from-rose-50 to-pink-50 p-6 rounded-xl border">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4">
                  <Clock className="w-5 h-5 text-rose-600" />
                  Hours Badge Colors
                </h3>
                <div className="bg-white p-4 rounded-lg border">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      { key: 'hoursBadgeBackgroundColor', label: 'Background', default: '#ffffff' },
                      { key: 'hoursBadgeTextColor', label: 'Text', default: '#5c3618' },
                      { key: 'hoursBadgeHighlightColor', label: 'Highlight', default: '#d17b34' }
                    ].map(item => (
                      <div key={item.key}>
                        <div className="flex items-center gap-2 mb-2">
                          <div
                            className="w-6 h-6 rounded-full border-2 border-gray-300"
                            style={{ backgroundColor: content?.[item.key] || item.default }}
                          ></div>
                          <label className="text-xs font-medium text-gray-600">{item.label}</label>
                        </div>
                        <ColorPicker
                          color={content?.[item.key] || item.default}
                          onChange={(color) => updateContent(item.key, color)}
                        />
                      </div>
                    ))}
                  </div>
                  {/* Hours Badge Preview */}
                  <div className="mt-4">
                    <div
                      className="inline-block px-3 py-1 rounded-full text-sm"
                      style={{
                        backgroundColor: content?.hoursBadgeBackgroundColor || '#ffffff',
                        color: content?.hoursBadgeTextColor || '#5c3618'
                      }}
                    >
                      Open Today: <span style={{ color: content?.hoursBadgeHighlightColor || '#d17b34' }}>7:00 AM - 10:00 PM</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Category & Card Colors */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Category Colors */}
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-6 rounded-xl border">
                  <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4">
                    <Grid className="w-5 h-5 text-purple-600" />
                    Categories
                  </h3>
                  <div className="space-y-3">
                    {[
                      { key: 'categoryBackgroundColor', label: 'Background', default: '#5c3618' },
                      { key: 'categoryTextColor', label: 'Text', default: '#ffffff' },
                      { key: 'categoryIconBackgroundColor', label: 'Icon BG', default: '#ffffff' }
                    ].map(item => (
                      <div key={item.key} className="flex items-center gap-2">
                        <div
                          className="w-6 h-6 rounded border-2 border-gray-300"
                          style={{ backgroundColor: content?.[item.key] || item.default }}
                        ></div>
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">{item.label}</label>
                          <ColorPicker
                            color={content?.[item.key] || item.default}
                            onChange={(color) => updateContent(item.key, color)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Overlay Card Colors */}
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-6 rounded-xl border">
                  <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4">
                    <Layers className="w-5 h-5 text-emerald-600" />
                    Overlay Card
                  </h3>
                  <div className="space-y-3">
                    {[
                      { key: 'overlayCardBackgroundColor', label: 'Background', default: 'rgba(255, 255, 255, 0.9)', allowAlpha: true },
                      { key: 'overlayCardTitleColor', label: 'Title', default: '#5c3618' },
                      { key: 'overlayCardTextColor', label: 'Text', default: '#6b5c4d' },
                      { key: 'overlayCardTaglineColor', label: 'Tagline', default: '#d17b34' }
                    ].map(item => (
                      <div key={item.key} className="flex items-center gap-2">
                        <div
                          className="w-6 h-6 rounded border-2 border-gray-300"
                          style={{ backgroundColor: content?.[item.key] || item.default }}
                        ></div>
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">{item.label}</label>
                          <ColorPicker
                            color={content?.[item.key] || item.default}
                            onChange={(color) => updateContent(item.key, color)}
                            allowAlpha={item.allowAlpha}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Button Style Customization */}
              <div className="space-y-4">
                {['primary', 'secondary', 'third', 'fourth'].map(buttonType => {
                  const hasText = content?.[`${buttonType}ButtonText`];
                  if (buttonType === 'third' && !hasText) return null;
                  if (buttonType === 'fourth' && !hasText) return null;

                  return (
                    <div key={buttonType} className="bg-white p-6 rounded-xl border">
                      <button
                        onClick={() => toggleSection(`${buttonType}Button`)}
                        className="w-full flex items-center justify-between text-left"
                      >
                        <h4 className="font-medium text-gray-800 capitalize">{buttonType} Button Styling</h4>
                        {expandedSections[`${buttonType}Button`] ?
                          <ChevronUp className="w-5 h-5 text-gray-500" /> :
                          <ChevronDown className="w-5 h-5 text-gray-500" />
                        }
                      </button>

                      {expandedSections[`${buttonType}Button`] && (
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Background Type</label>
                            <select
                              value={content?.[`${buttonType}ButtonBgType`] || (buttonType === 'primary' ? 'solid' : 'transparent')}
                              onChange={(e) => updateContent(`${buttonType}ButtonBgType`, e.target.value)}
                              className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:ring-0"
                            >
                              <option value="solid">Solid Color</option>
                              <option value="transparent">Transparent</option>
                              <option value="gradient">Gradient</option>
                            </select>
                          </div>

                          {content?.[`${buttonType}ButtonBgType`] !== 'transparent' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Opacity</label>
                              <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.1"
                                className="w-full"
                                value={content?.[`${buttonType}ButtonOpacity`] || '1'}
                                onChange={(e) => updateContent(`${buttonType}ButtonOpacity`, e.target.value)}
                              />
                              <div className="flex justify-between text-xs mt-1">
                                <span>Transparent</span>
                                <span>Opaque</span>
                              </div>
                            </div>
                          )}

                          {content?.[`${buttonType}ButtonBgType`] === 'gradient' && (
                            <>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Gradient From</label>
                                <ColorPicker
                                  color={content?.[`${buttonType}ButtonGradientFrom`] || '#d17b34'}
                                  onChange={(color) => updateContent(`${buttonType}ButtonGradientFrom`, color)}
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Gradient To</label>
                                <ColorPicker
                                  color={content?.[`${buttonType}ButtonGradientTo`] || '#e8924a'}
                                  onChange={(color) => updateContent(`${buttonType}ButtonGradientTo`, color)}
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Direction</label>
                                <select
                                  value={content?.[`${buttonType}ButtonGradientDirection`] || 'to right'}
                                  onChange={(e) => updateContent(`${buttonType}ButtonGradientDirection`, e.target.value)}
                                  className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:ring-0"
                                >
                                  <option value="to right">Left to Right</option>
                                  <option value="to left">Right to Left</option>
                                  <option value="to bottom">Top to Bottom</option>
                                  <option value="to top">Bottom to Top</option>
                                  <option value="to bottom right">Top Left to Bottom Right</option>
                                  <option value="to bottom left">Top Right to Bottom Left</option>
                                </select>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Category Colors */}
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-6 rounded-xl border">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4">
                  <Grid className="w-5 h-5 text-purple-600" />
                  Category Section Colors
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Background</label>
                    <ColorPicker
                      color={content?.categoryBackgroundColor || '#5c3618'}
                      onChange={(color) => updateContent('categoryBackgroundColor', color)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Text Color</label>
                    <ColorPicker
                      color={content?.categoryTextColor || '#ffffff'}
                      onChange={(color) => updateContent('categoryTextColor', color)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Icon Background</label>
                    <ColorPicker
                      color={content?.categoryIconBackgroundColor || '#ffffff'}
                      onChange={(color) => updateContent('categoryIconBackgroundColor', color)}
                    />
                  </div>
                </div>
              </div>

              {/* Overlay Card Colors */}
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-6 rounded-xl border">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4">
                  <Layers className="w-5 h-5 text-emerald-600" />
                  Overlay Card Colors
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Background</label>
                    <ColorPicker
                      color={content?.overlayCardBackgroundColor || 'rgba(255, 255, 255, 0.9)'}
                      onChange={(color) => updateContent('overlayCardBackgroundColor', color)}
                      allowAlpha={true}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Title Color</label>
                    <ColorPicker
                      color={content?.overlayCardTitleColor || '#5c3618'}
                      onChange={(color) => updateContent('overlayCardTitleColor', color)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Text Color</label>
                    <ColorPicker
                      color={content?.overlayCardTextColor || '#6b5c4d'}
                      onChange={(color) => updateContent('overlayCardTextColor', color)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tagline Color</label>
                    <ColorPicker
                      color={content?.overlayCardTaglineColor || '#d17b34'}
                      onChange={(color) => updateContent('overlayCardTaglineColor', color)}
                    />
                  </div>
                </div>
              </div>

              {/* Hours Badge Colors */}
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-6 rounded-xl border">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4">
                  <Clock className="w-5 h-5 text-orange-600" />
                  Hours Badge Colors
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Background</label>
                    <ColorPicker
                      color={content?.hoursBadgeBackgroundColor || '#ffffff'}
                      onChange={(color) => updateContent('hoursBadgeBackgroundColor', color)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Text Color</label>
                    <ColorPicker
                      color={content?.hoursBadgeTextColor || '#5c3618'}
                      onChange={(color) => updateContent('hoursBadgeTextColor', color)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Highlight</label>
                    <ColorPicker
                      color={content?.hoursBadgeHighlightColor || '#d17b34'}
                      onChange={(color) => updateContent('hoursBadgeHighlightColor', color)}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Media Tab */}
          {activeTab === 'media' && (
            <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
              {/* Main Images */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4">
                  <Image className="w-5 h-5 text-indigo-600" />
                  Main Images
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">Hero Image</label>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={content?.mainImage || '/images/pastry/bread-hero.webp'}
                          onChange={(e) => updateContent('mainImage', e.target.value)}
                          className="flex-1 p-3 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:ring-0"
                          placeholder="Image URL..."
                        />
                        <button
                          onClick={() => openMediaLibrary('mainImage', 'image')}
                          className="bg-indigo-600 text-white px-4 py-3 rounded-lg hover:bg-indigo-700 transition-colors"
                        >
                          Browse
                        </button>
                      </div>
                      {content?.mainImage && (
                        <div className="relative h-32 w-full overflow-hidden rounded-lg border">
                          <img
                            src={content.mainImage}
                            alt="Main Image Preview"
                            className="h-full w-full object-cover"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">Company Icon</label>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={content?.breadIcon || '/images/pastry/bread-icon.png'}
                          onChange={(e) => updateContent('breadIcon', e.target.value)}
                          className="flex-1 p-3 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:ring-0"
                          placeholder="Icon URL..."
                        />
                        <button
                          onClick={() => openMediaLibrary('breadIcon', 'image')}
                          className="bg-indigo-600 text-white px-4 py-3 rounded-lg hover:bg-indigo-700 transition-colors"
                        >
                          Browse
                        </button>
                      </div>
                      {content?.breadIcon && (
                        <div className="relative h-20 w-20 overflow-hidden rounded-lg border">
                          <img
                            src={content.breadIcon}
                            alt="Icon Preview"
                            className="h-full w-full object-contain"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Left Section Background */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl border">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4">
                  <Monitor className="w-5 h-5 text-emerald-600" />
                  Left Section Background
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Background Type</label>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { value: 'color', label: 'Color', icon: Palette },
                        { value: 'image', label: 'Image', icon: Image },
                        { value: 'video', label: 'Video', icon: Play }
                      ].map(type => (
                        <button
                          key={type.value}
                          onClick={() => updateContent('leftSectionType', type.value)}
                          className={`p-4 rounded-lg border-2 transition-all ${content?.leftSectionType === type.value
                              ? 'border-emerald-500 bg-emerald-50'
                              : 'border-gray-200 hover:border-gray-300'
                            }`}
                        >
                          <type.icon className="w-6 h-6 mx-auto mb-2 text-gray-600" />
                          <div className="text-sm font-medium">{type.label}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {content?.leftSectionType === 'image' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Background Image</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={content?.leftBackgroundImage || ''}
                          onChange={(e) => updateContent('leftBackgroundImage', e.target.value)}
                          className="flex-1 p-3 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:ring-0"
                          placeholder="Image URL..."
                        />
                        <button
                          onClick={() => openMediaLibrary('leftBackgroundImage', 'image')}
                          className="bg-emerald-600 text-white px-4 py-3 rounded-lg hover:bg-emerald-700 transition-colors"
                        >
                          Browse
                        </button>
                      </div>
                      {content?.leftBackgroundImage && (
                        <div className="mt-3 relative h-32 w-full overflow-hidden rounded-lg border">
                          <img
                            src={content.leftBackgroundImage}
                            alt="Left Background Preview"
                            className="h-full w-full object-cover"
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {content?.leftSectionType === 'video' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Background Video</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={content?.leftBackgroundVideo || ''}
                          onChange={(e) => updateContent('leftBackgroundVideo', e.target.value)}
                          className="flex-1 p-3 border-2 border-gray-200 rounded-lg focus:border-emerald-500 focus:ring-0"
                          placeholder="Video URL..."
                        />
                        <button
                          onClick={() => openMediaLibrary('leftBackgroundVideo', 'video')}
                          className="bg-emerald-600 text-white px-4 py-3 rounded-lg hover:bg-emerald-700 transition-colors"
                        >
                          Browse
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">Supported: MP4, WebM, OGG (max 100MB)</p>
                    </div>
                  )}

                  {(content?.leftSectionType === 'image' || content?.leftSectionType === 'video') && (
                    <div className="bg-white p-4 rounded-lg border">
                      <div className="flex items-center mb-3">
                        <input
                          type="checkbox"
                          checked={content?.leftOverlayEnabled || false}
                          onChange={(e) => updateContent('leftOverlayEnabled', e.target.checked)}
                          className="mr-2 w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                        />
                        <span className="text-sm font-medium">Enable overlay for better text visibility</span>
                      </div>

                      {content?.leftOverlayEnabled && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-2">Overlay Color</label>
                            <ColorPicker
                              color={content?.leftOverlayColor || 'rgba(248, 243, 233, 0.8)'}
                              onChange={(color) => updateContent('leftOverlayColor', color)}
                              allowAlpha={true}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-2">Opacity</label>
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.1"
                              className="w-full"
                              value={content?.leftOverlayOpacity || '0.8'}
                              onChange={(e) => updateContent('leftOverlayOpacity', e.target.value)}
                            />
                            <div className="flex justify-between text-xs">
                              <span>Transparent</span>
                              <span>Opaque</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Floating Images */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-xl border">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                  Floating Images
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[1, 2].map(num => (
                    <div key={num} className="bg-white p-4 rounded-lg border">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-800">Floating Image {num}</h4>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={content?.[`floatingImage${num}Visible`] !== false}
                            onChange={(e) => updateContent(`floatingImage${num}Visible`, e.target.checked)}
                            className="mr-2 w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                          />
                          <span className="text-sm">Show</span>
                        </label>
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={content?.[`floatingImage${num}`] || (num === 1 ? '/images/pastry/croissant.png' : '/images/pastry/bread.png')}
                            onChange={(e) => updateContent(`floatingImage${num}`, e.target.value)}
                            className="flex-1 p-2 border rounded focus:border-purple-500 focus:ring-0 text-sm"
                            placeholder="Image URL..."
                          />
                          <button
                            onClick={() => openMediaLibrary(`floatingImage${num}`, 'image')}
                            className="bg-purple-600 text-white px-3 py-2 rounded hover:bg-purple-700 transition-colors text-xs"
                          >
                            Browse
                          </button>
                        </div>
                        {content?.[`floatingImage${num}`] && (
                          <div className="relative h-20 w-20 overflow-hidden rounded border">
                            <img
                              src={content[`floatingImage${num}`]}
                              alt={`Floating Image ${num} Preview`}
                              className="h-full w-full object-contain"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Layout Tab */}
          {activeTab === 'layout' && (
            <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
              {/* Column Layout */}
              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-6 rounded-xl border">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4">
                  <Layout className="w-5 h-5 text-indigo-600" />
                  Column Layout
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">Left Column Width</label>
                    <div className="grid grid-cols-2 gap-2">
                      {layoutOptions.map(option => (
                        <button
                          key={option.value}
                          onClick={() => updateContent('leftColumnWidth', option.value)}
                          className={`p-3 rounded-lg border-2 transition-all ${content?.leftColumnWidth === option.value
                              ? 'border-indigo-500 bg-indigo-50'
                              : 'border-gray-200 hover:border-gray-300'
                            }`}
                        >
                          <div className="text-sm font-medium mb-2">{option.name}</div>
                          <div className="h-2 bg-gray-200 rounded">
                            <div className={`h-full bg-indigo-500 rounded ${option.visual}`}></div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">Right Column Width</label>
                    <div className="grid grid-cols-2 gap-2">
                      {layoutOptions.map(option => (
                        <button
                          key={option.value}
                          onClick={() => updateContent('rightColumnWidth', option.value)}
                          className={`p-3 rounded-lg border-2 transition-all ${content?.rightColumnWidth === option.value
                              ? 'border-indigo-500 bg-indigo-50'
                              : 'border-gray-200 hover:border-gray-300'
                            }`}
                        >
                          <div className="text-sm font-medium mb-2">{option.name}</div>
                          <div className="h-2 bg-gray-200 rounded">
                            <div className={`h-full bg-indigo-500 rounded ${option.visual}`}></div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Content Alignment */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl border">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4">
                  <AlignCenter className="w-5 h-5 text-emerald-600" />
                  Content Alignment
                </h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Content Alignment (when left column is 100%)
                  </label>
                  <div className="flex gap-3">
                    {[
                      { value: 'left', label: 'Left Aligned', icon: AlignLeft },
                      { value: 'center', label: 'Center Aligned', icon: AlignCenter }
                    ].map(alignment => (
                      <button
                        key={alignment.value}
                        onClick={() => updateContent('contentAlignment', alignment.value)}
                        className={`flex-1 flex items-center justify-center gap-2 p-4 rounded-lg border-2 transition-all ${content?.contentAlignment === alignment.value
                            ? 'border-emerald-500 bg-emerald-50'
                            : 'border-gray-200 hover:border-gray-300'
                          }`}
                      >
                        <alignment.icon className="w-5 h-5" />
                        {alignment.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Animation Settings */}
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-6 rounded-xl border">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800 mb-4">
                  <Sparkles className="w-5 h-5 text-orange-600" />
                  Animation & Effects
                </h3>

                <div className="space-y-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={content?.enableAnimations !== false}
                      onChange={(e) => updateContent('enableAnimations', e.target.checked)}
                      className="mr-3 w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                    />
                    <div>
                      <span className="text-sm font-medium">Enable Animations</span>
                      <p className="text-xs text-gray-500">Add smooth transitions and hover effects</p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Preview Section */}
        {showPreview && (
          <div className="lg:w-96 bg-gray-50 p-6 border-l">
            <div className="sticky top-6">
              <h3 className="font-bold text-lg mb-4 text-gray-800">Live Preview</h3>
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                {/* Split Layout Container */}
                <div className="flex min-h-[400px]">
                  {/* Left Section */}
                  <div
                    className={`${content?.leftColumnWidth === 'full' ? 'w-full' :
                        content?.leftColumnWidth === '3/4' ? 'w-3/4' :
                          content?.leftColumnWidth === '2/3' ? 'w-2/3' :
                            content?.leftColumnWidth === '1/2' ? 'w-1/2' :
                              content?.leftColumnWidth === '1/3' ? 'w-1/3' :
                                content?.leftColumnWidth === '1/4' ? 'w-1/4' :
                                  content?.leftColumnWidth === '0' ? 'w-0 hidden' : 'w-1/2'
                      } p-4 flex flex-col justify-center relative overflow-hidden`}
                    style={{
                      backgroundColor: content?.leftSectionType === 'color' ?
                        (content?.leftBackgroundColor || '#f8f3e9') : 'transparent',
                      backgroundImage: content?.leftSectionType === 'image' && content?.leftBackgroundImage ?
                        `url(${content.leftBackgroundImage})` : 'none',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  >
                    {/* Video Background */}
                    {content?.leftSectionType === 'video' && content?.leftBackgroundVideo && (
                      <video
                        className="absolute inset-0 w-full h-full object-cover"
                        autoPlay
                        muted
                        loop
                        src={content.leftBackgroundVideo}
                      />
                    )}

                    {/* Overlay */}
                    {content?.leftOverlayEnabled && (content?.leftSectionType === 'image' || content?.leftSectionType === 'video') && (
                      <div
                        className="absolute inset-0"
                        style={{
                          backgroundColor: content?.leftOverlayColor || 'rgba(248, 243, 233, 0.8)',
                          opacity: content?.leftOverlayOpacity || 0.8
                        }}
                      />
                    )}

                    {/* Content */}
                    <div className={`relative z-10 ${content?.leftColumnWidth === 'full' && content?.contentAlignment === 'center' ? 'text-center' :
                        content?.leftColumnWidth === 'full' ? 'text-left' : 'text-left'
                      }`}>
                      {/* Company Icon */}
                      {content?.iconVisible !== false && content?.breadIcon && (
                        <div className="mb-3">
                          <img
                            src={content.breadIcon}
                            alt="Company Icon"
                            className="w-8 h-8 object-contain"
                          />
                        </div>
                      )}

                      {/* Title */}
                      {content?.title && (
                        <h1
                          className="text-lg font-bold mb-2 leading-tight"
                          style={{ color: content?.titleColor || '#5c3618' }}
                          dangerouslySetInnerHTML={{
                            __html: content.title
                              .replace(/<span>/g, `<span style="color: ${content?.highlightColor || '#d17b34'}">`)
                              .replace(/<br\/>/g, '<br>')
                          }}
                        />
                      )}

                      {/* Subtitle */}
                      {content?.subtitle && (
                        <p
                          className="text-sm mb-4 leading-relaxed"
                          style={{ color: content?.subtitleColor || '#6b5c4d' }}
                        >
                          {content.subtitle}
                        </p>
                      )}

                      {/* Buttons */}
                      <div className={`space-y-2 ${content?.mobileButtonLayout === 'vertical' ? 'flex-col' :
                          content?.buttonLayoutExtended === 'extended' ? 'flex flex-wrap gap-2' : 'flex flex-wrap gap-2'
                        }`}>
                        {content?.primaryButtonText && (
                          <div
                            className={`inline-block px-3 py-2 text-xs font-medium transition-colors ${content?.buttonStyle === 'square' ? 'rounded-lg' : 'rounded-full'
                              }`}
                            style={{
                              backgroundColor: content?.primaryButtonBgType === 'gradient' ?
                                `linear-gradient(${content?.primaryButtonGradientDirection || 'to right'}, ${content?.primaryButtonGradientFrom || content?.primaryButtonBackgroundColor || '#d17b34'}, ${content?.primaryButtonGradientTo || '#e8924a'})` :
                                content?.primaryButtonBgType === 'transparent' ? 'transparent' :
                                  (content?.primaryButtonBackgroundColor || '#d17b34'),
                              color: content?.primaryButtonTextColor || '#ffffff',
                              opacity: content?.primaryButtonOpacity || 1,
                              height: content?.buttonHeight ? content.buttonHeight : 'auto'
                            }}
                          >
                            {content?.primaryButtonIcon && (
                              <img
                                src={content.primaryButtonIcon}
                                alt=""
                                className="inline w-3 h-3 mr-1"
                                style={{
                                  width: content?.buttonIconSize || '12px',
                                  height: content?.buttonIconSize || '12px'
                                }}
                              />
                            )}
                            {content.primaryButtonText}
                          </div>
                        )}

                        {content?.secondaryButtonText && (
                          <div
                            className={`inline-block px-3 py-2 text-xs font-medium border transition-colors ml-2 ${content?.buttonStyle === 'square' ? 'rounded-lg' : 'rounded-full'
                              }`}
                            style={{
                              backgroundColor: content?.secondaryButtonBgType === 'gradient' ?
                                `linear-gradient(${content?.secondaryButtonGradientDirection || 'to right'}, ${content?.secondaryButtonGradientFrom || content?.secondaryButtonBorderColor || '#d17b34'}, ${content?.secondaryButtonGradientTo || '#e8924a'})` :
                                content?.secondaryButtonBgType === 'solid' ?
                                  (content?.secondaryButtonBackgroundColor || 'transparent') : 'transparent',
                              borderColor: content?.secondaryButtonBorderColor || '#d17b34',
                              color: content?.secondaryButtonTextColor || '#d17b34',
                              opacity: content?.secondaryButtonOpacity || 1,
                              height: content?.buttonHeight ? content.buttonHeight : 'auto'
                            }}
                          >
                            {content?.secondaryButtonIcon && (
                              <img
                                src={content.secondaryButtonIcon}
                                alt=""
                                className="inline w-3 h-3 mr-1"
                                style={{
                                  width: content?.buttonIconSize || '12px',
                                  height: content?.buttonIconSize || '12px'
                                }}
                              />
                            )}
                            {content.secondaryButtonText}
                          </div>
                        )}

                        {content?.thirdButtonText && (
                          <div
                            className={`inline-block px-3 py-2 text-xs font-medium border transition-colors ml-2 ${content?.buttonStyle === 'square' ? 'rounded-lg' : 'rounded-full'
                              }`}
                            style={{
                              backgroundColor: content?.thirdButtonBgType === 'gradient' ?
                                `linear-gradient(${content?.thirdButtonGradientDirection || 'to right'}, ${content?.thirdButtonGradientFrom || '#d17b34'}, ${content?.thirdButtonGradientTo || '#e8924a'})` :
                                content?.thirdButtonBgType === 'solid' ?
                                  (content?.thirdButtonBackgroundColor || 'transparent') : 'transparent',
                              borderColor: content?.secondaryButtonBorderColor || '#d17b34',
                              color: content?.secondaryButtonTextColor || '#d17b34',
                              opacity: content?.thirdButtonOpacity || 1
                            }}
                          >
                            {content?.thirdButtonIcon && (
                              <img
                                src={content.thirdButtonIcon}
                                alt=""
                                className="inline w-3 h-3 mr-1"
                                style={{
                                  width: content?.buttonIconSize || '12px',
                                  height: content?.buttonIconSize || '12px'
                                }}
                              />
                            )}
                            {content.thirdButtonText}
                          </div>
                        )}

                        {content?.fourthButtonText && (
                          <div
                            className={`inline-block px-3 py-2 text-xs font-medium transition-colors ml-2 ${content?.buttonStyle === 'square' ? 'rounded-lg' : 'rounded-full'
                              }`}
                            style={{
                              backgroundColor: content?.fourthButtonBgType === 'gradient' ?
                                `linear-gradient(${content?.fourthButtonGradientDirection || 'to right'}, ${content?.fourthButtonGradientFrom || '#d17b34'}, ${content?.fourthButtonGradientTo || '#e8924a'})` :
                                content?.fourthButtonBgType === 'solid' ?
                                  (content?.fourthButtonBackgroundColor || content?.primaryButtonBackgroundColor || '#d17b34') :
                                  'transparent',
                              color: content?.primaryButtonTextColor || '#ffffff',
                              opacity: content?.fourthButtonOpacity || 1,
                              border: content?.fourthButtonBgType === 'transparent' ?
                                `2px solid ${content?.secondaryButtonBorderColor || '#d17b34'}` : 'none'
                            }}
                          >
                            {content?.fourthButtonIcon && (
                              <img
                                src={content.fourthButtonIcon}
                                alt=""
                                className="inline w-3 h-3 mr-1"
                                style={{
                                  width: content?.buttonIconSize || '12px',
                                  height: content?.buttonIconSize || '12px'
                                }}
                              />
                            )}
                            {content.fourthButtonText}
                          </div>
                        )}
                      </div>

                      {/* Hours Badge */}
                      {content?.hoursBadgeVisible !== false && content?.hoursText && (
                        <div
                          className="inline-block mt-3 px-3 py-1 rounded-full text-xs shadow-sm"
                          style={{
                            backgroundColor: content?.hoursBadgeBackgroundColor || '#ffffff',
                            color: content?.hoursBadgeTextColor || '#5c3618'
                          }}
                          dangerouslySetInnerHTML={{
                            __html: content.hoursText.replace(/<span>/g, `<span style="color: ${content?.hoursBadgeHighlightColor || '#d17b34'}">`)
                          }}
                        />
                      )}
                    </div>

                    {/* Floating Images */}
                    {content?.floatingImage1Visible !== false && content?.floatingImage1 && (
                      <img
                        src={content.floatingImage1}
                        alt="Floating 1"
                        className="absolute top-2 right-2 w-8 h-8 object-contain opacity-80"
                      />
                    )}
                    {content?.floatingImage2Visible !== false && content?.floatingImage2 && (
                      <img
                        src={content.floatingImage2}
                        alt="Floating 2"
                        className="absolute bottom-2 left-2 w-6 h-6 object-contain opacity-70"
                      />
                    )}
                  </div>

                  {/* Right Section */}
                  {content?.rightColumnWidth !== '0' && (
                    <div
                      className={`${content?.rightColumnWidth === 'full' ? 'w-full' :
                          content?.rightColumnWidth === '3/4' ? 'w-3/4' :
                            content?.rightColumnWidth === '2/3' ? 'w-2/3' :
                              content?.rightColumnWidth === '1/2' ? 'w-1/2' :
                                content?.rightColumnWidth === '1/3' ? 'w-1/3' :
                                  content?.rightColumnWidth === '1/4' ? 'w-1/4' : 'w-1/2'
                        } p-4 flex items-center justify-center relative`}
                      style={{ backgroundColor: content?.rightBackgroundColor || '#5c3618' }}
                    >
                      {content?.mainImage ? (
                        <img
                          src={content.mainImage}
                          alt="Main Preview"
                          className="max-w-full h-32 object-cover rounded-lg shadow-lg"
                        />
                      ) : (
                        <div className="w-full h-32 bg-gray-200 rounded-lg flex items-center justify-center">
                          <Image className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Overlay Card */}
                {content?.overlayCardVisible !== false && (content?.overlayCardTitle || content?.overlayCardText) && (
                  <div
                    className="absolute bottom-4 left-4 right-4 p-3 rounded-lg shadow-lg backdrop-blur-sm"
                    style={{
                      backgroundColor: content?.overlayCardBackgroundColor || 'rgba(255, 255, 255, 0.9)'
                    }}
                  >
                    {content?.overlayCardTitle && (
                      <h4
                        className="font-medium text-sm mb-1"
                        style={{ color: content?.overlayCardTitleColor || '#5c3618' }}
                      >
                        {content.overlayCardTitle}
                      </h4>
                    )}
                    {content?.overlayCardText && (
                      <div
                        className="text-xs mb-1 leading-relaxed"
                        style={{ color: content?.overlayCardTextColor || '#6b5c4d' }}
                        dangerouslySetInnerHTML={{ __html: content.overlayCardText }}
                      />
                    )}
                    {content?.overlayCardTagline && (
                      <p
                        className="text-xs font-medium"
                        style={{ color: content?.overlayCardTaglineColor || '#d17b34' }}
                      >
                        {content.overlayCardTagline}
                      </p>
                    )}
                  </div>
                )}

                {/* Categories Section */}
                {content?.showCategories !== false && content?.categoryItems?.length > 0 && (
                  <div
                    className="p-4 border-t"
                    style={{ backgroundColor: content?.categoryBackgroundColor || '#5c3618' }}
                  >
                    <div className="grid grid-cols-2 gap-2">
                      {content.categoryItems.slice(0, 4).map((item, index) => (
                        <div key={index} className="text-center">
                          {item.imageUrl && (
                            <div
                              className="w-8 h-8 rounded-full mx-auto mb-1 flex items-center justify-center"
                              style={{ backgroundColor: content?.categoryIconBackgroundColor || '#ffffff' }}
                            >
                              <img
                                src={item.imageUrl}
                                alt={item.name || `Category ${index + 1}`}
                                className="w-4 h-4 object-contain"
                              />
                            </div>
                          )}
                          {item.name && (
                            <p
                              className="text-xs truncate"
                              style={{ color: content?.categoryTextColor || '#ffffff' }}
                            >
                              {item.name}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-2 text-center">
                Live preview updates as you make changes
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HeaderDividedForm;